import * as THREE from "three";
import type { RoomState } from "../viewer/HouseComposerView.ts";

const WALL_MAX_NY = 0.30;       // |normal.y| above this = floor/ceiling, skip
const COPLANAR_RADIUS = 1.5;    // metres — search radius for coplanar faces
const COPLANAR_DOT = 0.97;      // cos ~14° — normal similarity threshold
const SNAP_RELEASE_DIST = 0.20; // m — drift beyond this releases the snap constraint

const COLOR_A = 0x00e676;  // green — wall A (anchor)
const COLOR_B = 0x448aff;  // blue  — wall B (will move)
const HELPER_W = 2.5;
const HELPER_H = 3.0;

export type WallSnapState = "inactive" | "pickA" | "pickB" | "snapped";

interface WallPlane {
  roomIndex: number;
  /** Unit normal in world space at pick time. */
  worldNormal: THREE.Vector3;
  /** Plane equation offset: worldNormal · worldCentroid at pick time. */
  d: number;
  /** Centroid expressed in room group local space.
   *  Used to re-derive the world centroid position after rotation. */
  groupLocalCentroid: THREE.Vector3;
}

export class WallSnapTool {
  private _active = false;
  private _phase: "pickA" | "pickB" = "pickA";
  private _wallA: WallPlane | null = null;
  private _wallB: WallPlane | null = null;

  /** True once snap has been applied and the constraint is live. */
  snapActive = false;
  /** Index of the room being constrained (room B). */
  snapRoomIndex = -1;
  /** World-space normal of the snap plane (= wallA.worldNormal). */
  snapNormal: THREE.Vector3 | null = null;
  /** Target value of snapNormal · roomB.group.position after snap. */
  snapDotTarget = 0;

  private readonly _helperGroup = new THREE.Group();
  private _helperA: THREE.Mesh | null = null;
  private _helperB: THREE.Mesh | null = null;

  private readonly _raycaster = new THREE.Raycaster();
  private readonly _onClickBound = this._onClick.bind(this);

  onStateChange: ((state: WallSnapState) => void) | null = null;
  /** Called after snap so the caller can sync entry.transform. */
  onSnapApplied: ((roomIndex: number) => void) | null = null;

  constructor(
    private readonly _canvas: HTMLCanvasElement,
    private readonly _camera: THREE.Camera,
    private readonly _scene: THREE.Scene,
    private readonly _getRooms: () => RoomState[],
  ) {
    this._scene.add(this._helperGroup);
  }

  get isActive(): boolean { return this._active; }
  get state(): WallSnapState {
    if (!this._active) return "inactive";
    if (this.snapActive) return "snapped";
    return this._phase;
  }

  activate(): void {
    if (this._active) return;
    this._active = true;
    this._phase = "pickA";
    this._wallA = null;
    this._wallB = null;
    this.snapActive = false;
    this._clearHelpers();
    this._canvas.style.cursor = "crosshair";
    this._canvas.addEventListener("click", this._onClickBound, true);
    this._notify();
  }

  deactivate(): void {
    if (!this._active) return;
    this._active = false;
    this.snapActive = false;
    this._canvas.style.cursor = "";
    this._canvas.removeEventListener("click", this._onClickBound, true);
    this._wallA = null;
    this._wallB = null;
    this._clearHelpers();
    this._notify();
  }

  /**
   * Call after a drag ends. If room B drifted < SNAP_RELEASE_DIST from the
   * snap plane, it snaps back (returns true). Otherwise the constraint is
   * released (returns false) — user intentionally moved it away.
   */
  enforceSnap(rooms: RoomState[]): boolean {
    if (!this.snapActive || !this.snapNormal || this.snapRoomIndex < 0) return false;
    const room = rooms[this.snapRoomIndex];
    if (!room) return false;

    const drift = this.snapNormal.dot(room.group.position) - this.snapDotTarget;
    if (Math.abs(drift) <= SNAP_RELEASE_DIST) {
      room.group.position.sub(this.snapNormal.clone().multiplyScalar(drift));
      room.group.updateMatrix();
      return true;
    }
    // Drift too large — release
    this.snapActive = false;
    this.snapNormal = null;
    this._notify();
    return false;
  }

  dispose(): void {
    this.deactivate();
    this._helperGroup.removeFromParent();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _notify(): void {
    this.onStateChange?.(this.state);
  }

  private _onClick(e: MouseEvent): void {
    if (!this._active) return;

    const rect = this._canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this._raycaster.setFromCamera(ndc, this._camera);

    const rooms = this._getRooms();
    const meshes: THREE.Mesh[] = [];
    for (const r of rooms) {
      r.group.traverse((obj) => {
        if (obj instanceof THREE.Mesh && !obj.userData.isHorizontalSurface) meshes.push(obj);
      });
    }

    const hits = this._raycaster.intersectObjects(meshes, false);
    const hit = hits[0];
    if (!hit?.face) return;

    const mesh = hit.object as THREE.Mesh;
    mesh.updateWorldMatrix(true, false);

    // World-space normal of the hit face
    const worldNormal = hit.face.normal.clone()
      .transformDirection(mesh.matrixWorld)
      .normalize();

    // Reject floors and ceilings
    if (Math.abs(worldNormal.y) > WALL_MAX_NY) return;

    const roomIndex = this._findRoomIndex(mesh, rooms);
    if (roomIndex < 0) return;

    // Wall B must be from a different room than wall A
    if (this._phase === "pickB" && roomIndex === this._wallA?.roomIndex) return;

    const room = rooms[roomIndex];
    room.group.updateWorldMatrix(true, false);

    // Collect centroid of coplanar faces in mesh local space, then convert
    const centroidMeshLocal = this._collectCoplanarCentroid(
      mesh, hit.point, hit.face.normal,
    );
    const worldCentroid = mesh.localToWorld(centroidMeshLocal.clone());
    const groupLocalCentroid = room.group.worldToLocal(worldCentroid.clone());

    const plane: WallPlane = {
      roomIndex,
      worldNormal,
      d: worldNormal.dot(worldCentroid),
      groupLocalCentroid,
    };

    // Valid wall picked — consume the event
    e.stopPropagation();
    e.preventDefault();

    if (this._phase === "pickA") {
      this._wallA = plane;
      this._phase = "pickB";
      this._setHelper("A", worldCentroid, worldNormal);
      this._notify();
    } else {
      this._wallB = plane;
      this._applySnap(rooms);
    }
  }

  private _applySnap(rooms: RoomState[]): void {
    if (!this._wallA || !this._wallB) return;
    const roomB = rooms[this._wallB.roomIndex];
    if (!roomB) return;

    const n_a = this._wallA.worldNormal;
    const n_b = this._wallB.worldNormal; // picked before any rotation — use directly

    // ── Step 1: yaw rotation to make n_b antiparallel to n_a ──

    const from = new THREE.Vector3(n_b.x, 0, n_b.z).normalize();
    const to   = new THREE.Vector3(-n_a.x, 0, -n_a.z).normalize();

    roomB.group.rotation.y += this._yawBetween(from, to);
    roomB.group.updateMatrix();
    roomB.group.updateMatrixWorld(true);

    // ── Step 2: translate room B along n_a to make the planes coincide ──

    const c_b_world = roomB.group.localToWorld(this._wallB.groupLocalCentroid.clone());
    const dist = n_a.dot(c_b_world) - this._wallA.d;
    roomB.group.position.sub(n_a.clone().multiplyScalar(dist));
    roomB.group.updateMatrix();

    // ── Store snap constraint ──
    this.snapActive = true;
    this.snapRoomIndex = this._wallB.roomIndex;
    this.snapNormal = n_a.clone();
    this.snapDotTarget = n_a.dot(roomB.group.position);

    // ── Update visual helper B to snapped position ──
    roomB.group.updateMatrixWorld(true);
    const newCentroid = roomB.group.localToWorld(this._wallB.groupLocalCentroid.clone());
    this._setHelper("B", newCentroid, n_a.clone().negate());

    this._notify();
    this.onSnapApplied?.(this._wallB.roomIndex);
  }

  /**
   * Angle (Three.js rotation.y convention: positive = CW from above) to rotate
   * the XZ unit vector `from` to align with `to`.
   */
  private _yawBetween(from: THREE.Vector3, to: THREE.Vector3): number {
    const dot = Math.max(-1, Math.min(1, from.dot(to)));
    if (dot > 1 - 1e-4) return 0;
    if (dot < -1 + 1e-4) return Math.PI;
    // Y component of (from × to) — positive means from should rotate CW to reach to
    const cross = from.x * to.z - from.z * to.x;
    return -Math.atan2(cross, dot); // negate for Three.js CW convention
  }

  /**
   * Iterate the mesh geometry to find face centroids within COPLANAR_RADIUS of
   * the hit point that share roughly the same normal. Returns their average
   * centroid in mesh local space.
   */
  private _collectCoplanarCentroid(
    mesh: THREE.Mesh,
    hitWorldPoint: THREE.Vector3,
    hitFaceNormalLocal: THREE.Vector3, // hit.face.normal — already in mesh local space
  ): THREE.Vector3 {
    const geometry = mesh.geometry;
    const positions = geometry.attributes.position as THREE.BufferAttribute | undefined;
    const normals   = geometry.attributes.normal   as THREE.BufferAttribute | undefined;
    if (!positions || !normals) return mesh.worldToLocal(hitWorldPoint.clone());

    mesh.updateWorldMatrix(true, false);
    const hitLocal = mesh.worldToLocal(hitWorldPoint.clone());
    const refN = hitFaceNormalLocal.clone().normalize();
    const rSq  = COPLANAR_RADIUS * COPLANAR_RADIUS;
    const idx  = geometry.index;
    const count = idx ? idx.count / 3 : positions.count / 3;

    let sumX = 0, sumY = 0, sumZ = 0, found = 0;

    for (let fi = 0; fi < count; fi++) {
      const ia = idx ? idx.getX(fi * 3)     : fi * 3;
      const ib = idx ? idx.getX(fi * 3 + 1) : fi * 3 + 1;
      const ic = idx ? idx.getX(fi * 3 + 2) : fi * 3 + 2;

      const cx = (positions.getX(ia) + positions.getX(ib) + positions.getX(ic)) / 3;
      const cy = (positions.getY(ia) + positions.getY(ib) + positions.getY(ic)) / 3;
      const cz = (positions.getZ(ia) + positions.getZ(ib) + positions.getZ(ic)) / 3;

      const dx = cx - hitLocal.x, dy = cy - hitLocal.y, dz = cz - hitLocal.z;
      if (dx*dx + dy*dy + dz*dz > rSq) continue;

      const nx = (normals.getX(ia) + normals.getX(ib) + normals.getX(ic)) / 3;
      const ny = (normals.getY(ia) + normals.getY(ib) + normals.getY(ic)) / 3;
      const nz = (normals.getZ(ia) + normals.getZ(ib) + normals.getZ(ic)) / 3;
      const nLen = Math.sqrt(nx*nx + ny*ny + nz*nz);
      if (nLen < 1e-4) continue;

      const dot = (nx*refN.x + ny*refN.y + nz*refN.z) / nLen;
      if (dot < COPLANAR_DOT) continue;

      sumX += cx; sumY += cy; sumZ += cz;
      found++;
    }

    return found > 0
      ? new THREE.Vector3(sumX / found, sumY / found, sumZ / found)
      : hitLocal.clone();
  }

  private _findRoomIndex(mesh: THREE.Object3D, rooms: RoomState[]): number {
    let obj: THREE.Object3D | null = mesh;
    while (obj) {
      const idx = rooms.findIndex((r) => r.group === obj);
      if (idx >= 0) return idx;
      obj = obj.parent;
    }
    return -1;
  }

  private _setHelper(which: "A" | "B", centroid: THREE.Vector3, normal: THREE.Vector3): void {
    const color = which === "A" ? COLOR_A : COLOR_B;
    if (which === "A") {
      this._disposeHelper(this._helperA);
      this._helperA = this._buildHelper(centroid, normal, color);
      this._helperGroup.add(this._helperA);
    } else {
      this._disposeHelper(this._helperB);
      this._helperB = this._buildHelper(centroid, normal, color);
      this._helperGroup.add(this._helperB);
    }
  }

  private _buildHelper(centroid: THREE.Vector3, normal: THREE.Vector3, color: number): THREE.Mesh {
    const geo = new THREE.PlaneGeometry(HELPER_W, HELPER_H);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.30,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });
    const plane = new THREE.Mesh(geo, mat);
    plane.renderOrder = 999;
    plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal.clone().normalize());
    plane.position.copy(centroid);

    const edgeGeo = new THREE.EdgesGeometry(geo);
    const edgeMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.85 });
    const lines = new THREE.LineSegments(edgeGeo, edgeMat);
    lines.renderOrder = 1000;
    plane.add(lines);

    return plane;
  }

  private _disposeHelper(mesh: THREE.Mesh | null): void {
    if (!mesh) return;
    mesh.removeFromParent();
    mesh.geometry.dispose();
    if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose());
    else (mesh.material as THREE.Material).dispose();
    for (const c of mesh.children) {
      if (c instanceof THREE.LineSegments) {
        c.geometry.dispose();
        (c.material as THREE.Material).dispose();
      }
    }
  }

  private _clearHelpers(): void {
    this._disposeHelper(this._helperA); this._helperA = null;
    this._disposeHelper(this._helperB); this._helperB = null;
  }
}
